// material-ui
import { TypographyVariantsOptions } from '@mui/material/styles';

// types
import { FontFamily, FontSize } from 'types/config';

// ==============================|| DEFAULT THEME - TYPOGRAPHY  ||============================== //

export const FONT_SIZE_SCALE: Record<FontSize, number> = {
  small: 1.006,
  default: 1.15,
  large: 1.294,
  'extra-large': 1.438,
  'xx-large': 1.581
};

// The font-size setting scales the ROOT (html) font size, not the typography variants —
// so every rem-sized value in the app (theme overrides, tables, chips, inputs) scales
// with it, not just Typography text. Scales are normalized to 'default' so that setting
// renders exactly as before (html stays 16px, body text 16.096px).
export function htmlFontSize(fontSize: FontSize): string {
  const scale = (FONT_SIZE_SCALE[fontSize] ?? FONT_SIZE_SCALE.default) / FONT_SIZE_SCALE.default;
  return `${Math.round(scale * 16 * 1000) / 1000}px`;
}

// Every text variant renders at this single size: 0.875 × the old 'default' scale (1.15),
// expressed in rem so it follows the html font size above. Variants differ only by
// weight, not size.
const BASE_FONT_SIZE_REM = '1.006rem';

export default function Typography(fontFamily: FontFamily): TypographyVariantsOptions {
  const fontSizeRem = BASE_FONT_SIZE_REM;
  const lineHeight = 1.57;

  return {
    htmlFontSize: 16,
    fontFamily,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontWeight: 600,
      fontSize: fontSizeRem,
      lineHeight
    },
    h2: {
      fontWeight: 600,
      fontSize: fontSizeRem,
      lineHeight
    },
    h3: {
      fontWeight: 600,
      fontSize: fontSizeRem,
      lineHeight
    },
    h4: {
      fontWeight: 600,
      fontSize: fontSizeRem,
      lineHeight
    },
    h5: {
      fontWeight: 600,
      fontSize: fontSizeRem,
      lineHeight
    },
    h6: {
      fontWeight: 400,
      fontSize: fontSizeRem,
      lineHeight
    },
    caption: {
      fontWeight: 400,
      fontSize: fontSizeRem,
      lineHeight
    },
    body1: {
      fontSize: fontSizeRem,
      lineHeight
    },
    body2: {
      fontSize: fontSizeRem,
      lineHeight
    },
    subtitle1: {
      fontSize: fontSizeRem,
      fontWeight: 600,
      lineHeight
    },
    subtitle2: {
      fontSize: fontSizeRem,
      fontWeight: 500,
      lineHeight
    },
    overline: {
      fontSize: fontSizeRem,
      lineHeight
    },
    button: {
      fontSize: fontSizeRem,
      textTransform: 'capitalize'
    }
  };
}
